'use client';
import useNonProfitStore from '@components/InitNonProfitStore/store';
import { yupResolver } from '@hookform/resolvers/yup';
import Dialog from '@ui/Dialog';
import Typography from '@ui/Typography';
import { getISOByParam } from 'iso-country-currency';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { updateNonProfitProfile } from 'stores/nonProfitAdminActions';
import { getCountryName } from 'utils/stringHelpers';
import EditNpoProfileForm from './EditNpoProfileForm';
import useEditNpoProfileStore from 'components/store/editNpoProfileModalSlice';
import { authorizedServerFetch, revalidatePage } from 'actions/server.actions';
import StyledContainedButton from 'components/Styled/StyledContainedButton';
import * as yup from 'yup';

const schema = yup.object().shape({
  description: yup
    .string()
    .required('You must enter the description')
    .max(1000, 'Description must be at most 1000 characters long'),
  slogan: yup
    .string()
    .required('You must enter the slogan')
    .max(255, 'Slogan must be at most 255 characters long'),
  ein: yup
    .string()
    .required('You must enter the tax id')
    .max(30, 'EIN must be at most 30 characters long'),
  website: yup
    .string()
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Website url is not valid',
    )
    .required('Please enter website')
    .max(100, 'Website must be at most 100 characters long'),
  address: yup
    .string()
    .required('You must enter the address')
    .max(100, 'Address must be at most 100 characters long'),
  city: yup
    .string()
    .required('You must enter the city')
    .max(50, 'City must be at most 50 characters long'),
  state: yup
    .string()
    .required('You must enter the state')
    .max(50, 'State must be at most 50 characters long'),
  zipcode: yup
    .string()
    .required('You must enter the zipcode')
    .max(50, 'Zipcode must be at most 50 characters long'),
  country: yup.mixed().required('You must enter the country'),
  donationUrl: yup
    .string()
    .matches(
      /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/,
      'Donation URL is not valid',
    )
    .max(100, 'Donation Url must be at most 100 characters long')
    .nullable()
    .notRequired(),
  causes: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.number().required(),
      }),
    )
    .required('You must select at least one cause')
    .min(1, 'You must select at least one cause'),
});

function EditNpoProfileDialog() {
  const open = useEditNpoProfileStore((state) => state.open);
  const onClose = useEditNpoProfileStore((state) => state.setOpen);
  const setIsOpen = useEditNpoProfileStore((state) => state.setClose);
  const nonProfitData = useEditNpoProfileStore((state) => state.nonProfit);
  const initNonProfitData = useNonProfitStore((state) => state.nonProfit);
  const setNonProfit = useNonProfitStore((state) => state.setNonProfit);
  const setEditNonProfit = useEditNpoProfileStore(
    (state) => state.setEditNonProfit,
  );
  const [loading, setLoading] = useState(false);

  const nonProfit = nonProfitData || initNonProfitData;

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const updateNonProfitData = (npData) => {
    const npo = npData || nonProfit;
    if (open) {
      const defaultFormValues = {
        website: npo.website_url,
        address: npo.address?.replace(/^"|"$/g, ''),
        city: npo.city,
        state: npo.state,
        country: {
          id: getCountryName(npo.country || npo.country || ''),
          label: getCountryName(npo.country || npo.country || ''),
        },
        zipcode: npo.zipcode,
        description: npo.description,
        slogan: npo.slogan,
        ein: npo.ein,
        causes: npo.causes,
        donationUrl: npo?.donation_url || null,
      };

      formMethods.reset(defaultFormValues);
      formMethods.trigger(); // Ensure validation runs after resetting values
    }
  };

  useEffect(() => {
    updateNonProfitData();
  }, [open]);

  const handleSave = async (formValues) => {
    setLoading(true);
    const formData = new FormData();
    const payload = formValues;
    payload.country = getISOByParam('countryName', formValues.country.label);
    payload.causes = payload.causes.map((cause) => cause.id);
    formData.append('data', JSON.stringify(payload));

    const { data, ok } = await authorizedServerFetch(
      'npo-admin/organization/profile',
      {
        method: 'PUT',
        body: formData,
      },
    );

    console.log('EditNonProfitDialog', data);
    setLoading(false);
    if (ok) {
      updateNonProfitData(data?.data);
      setNonProfit(data?.data);
      setEditNonProfit(data?.data);
      // formMethods.reset(formValues);
      // await updateNonProfitProfile(data, nonProfit.id);
      // revalidatePage('/organization/[slug]');
    }
    // setLoading(false);
    // onClose(true, data?.data);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog isOpen={open} onClose={handleClose} maxWidth="lg">
      <div className="flex w-full flex-col gap-4 overflow-hidden p-0 lg:p-0">
        <div className="flex items-center justify-center text-center">
          <Typography variant="h1" className="uppercase" component="h2">
            Edit Profile
          </Typography>
        </div>
        <div className="flex flex-1 flex-col-reverse gap-4 overflow-y-auto overflow-x-hidden lg:flex-row">
          <FormProvider {...formMethods}>
            <EditNpoProfileForm onSubmit={handleSave} nonProfit={nonProfit} />
          </FormProvider>
        </div>

        <div className="flex-rows flex justify-end gap-4">
          <StyledContainedButton
            type="submit"
            form="update-np-overview-tab"
            disabled={!formMethods.formState.isValid || loading} // Disable button if form is invalid or loading
            loading={loading}>
            Save
          </StyledContainedButton>
        </div>
      </div>
    </Dialog>
  );
}

export default EditNpoProfileDialog;
